/**
 * Merges row metadata without aggregating numeric values
 * This preserves the ability for pivot accessors to find correct values via parent chains
 * @param {Object} target - Target row
 * @param {Object} source - Source row to merge
 * @returns {Object} Merged row
 */
const mergeRowMetadata = (target, source) => {
  const merged = { ...target }

  Object.keys(source).forEach(key => {
    if (key === 'data' || key === '__originalData' || key === '__parentChain' || key === '__allInstances') {
      return
    }

    const targetVal = target[key]
    const sourceVal = source[key]

    // Keep first non-null value for metadata fields (like levelName, grouping, ordinal)
    if ((targetVal === null || targetVal === undefined) && sourceVal !== null && sourceVal !== undefined) {
      merged[key] = sourceVal
    }
  })

  return merged
}

/**
 * Groups rows by identifier while preserving all parent contexts
 * This allows pivot columns to extract values based on their specific parent chain
 * @param {Array} rows - Rows to group
 * @param {string} identifierKey - Key to use for grouping (e.g., 'orionEntityId')
 * @param {Function} getSubRows - Function to get sub-rows
 * @returns {Array} Grouped rows (one row per unique identifier)
 */
const groupRowsByIdentifier = (rows, identifierKey, getSubRows) => {
  if (!identifierKey || !Array.isArray(rows) || rows.length === 0) {
    return rows
  }

  const rowMap = new Map()

  rows.forEach(row => {
    const identifier = row[identifierKey] || row.levelName

    if (!identifier) {
      const uniqueKey = `${Math.random()}_${Date.now()}`
      rowMap.set(uniqueKey, row)
      return
    }

    if (rowMap.has(identifier)) {
      const existing = rowMap.get(identifier)

      // Merge metadata but keep all instances for value extraction
      const mergedMetadata = mergeRowMetadata(existing, row)

      // Merge children
      const existingChildren = getSubRows(existing) || []
      const newChildren = getSubRows(row) || []
      const allChildren = [...existingChildren, ...newChildren]
      const groupedChildren = groupRowsByIdentifier(allChildren, identifierKey, getSubRows)

      // Store all instances to preserve parent chain contexts
      const allInstances = existing.__allInstances || [existing]
      allInstances.push(row)

      rowMap.set(identifier, {
        ...mergedMetadata,
        data: groupedChildren,
        __originalData: existing.__originalData || existing,
        __parentChain: existing.__parentChain || [],
        __allInstances: allInstances
      })
    } else {
      rowMap.set(identifier, {
        ...row,
        data: groupRowsByIdentifier(getSubRows(row) || [], identifierKey, getSubRows)
      })
    }
  })

  return Array.from(rowMap.values())
}

/**
 * Filters hierarchical data by grouping levels, promoting children when parents don't match
 * @param {Array} rows - The rows to filter
 * @param {Array} originalRows - The original unfiltered rows (for reference)
 * @param {Array} groupingLevels - The grouping levels to include
 * @param {Function} getSubRows - Function to get sub-rows from a row
 * @param {Array} parentChain - Chain of parent rows (for tracking ancestry)
 * @param {string} groupRowsBy - Optional key to group duplicate rows by (e.g., 'orionEntityId')
 * @returns {Array} Filtered rows
 */
export const filterByGrouping = (rows, originalRows, groupingLevels, getSubRows, parentChain = [], groupRowsBy = null) => {
  if (!Array.isArray(rows)) return rows

  const result = []

  rows.forEach((row, index) => {
    const originalRow = originalRows?.[index] || row
    const newParentChain = [...parentChain, originalRow]
    const childRows = getSubRows(row)
    const originalChildRows = getSubRows(originalRow)
    const filteredChildren = filterByGrouping(childRows, originalChildRows, groupingLevels, getSubRows, newParentChain, groupRowsBy)

    if (groupingLevels.includes(row.grouping)) {
      result.push({
        ...row,
        data: filteredChildren,
        __originalData: originalRow,
        __parentChain: parentChain
      })
    } else if (filteredChildren && filteredChildren.length > 0) {
      result.push(...filteredChildren)
    }
  })

  // Group duplicate rows if requested
  if (groupRowsBy) {
    return groupRowsByIdentifier(result, groupRowsBy, getSubRows)
  }

  return result
}
